import type { ReactNode } from 'react';
import { createContext, useContext } from 'react';
import { useAxiosInstance, usePoMutation } from '@gov-nx/api';
import { removeAccountQuery } from '@gov-nx/api';
import type { FormWizardHook } from '@gov-nx/core/hooks';
import { useWizard } from '@gov-nx/core/hooks';
import type { Nullable } from '@gov-nx/core/types';
import type { IUseBoolean } from '@gov-nx/utils/common';
import { useBoolean } from '@gov-nx/utils/common';
import type {
	SmazaniUctuFormData,
	SmazaniUctuFormStep1,
	SmazaniUctuFormStep2,
	SmazaniUctuFormStep3,
} from './useForm';
import { useFormStep1, useFormStep2, useFormStep3 } from './useForm';

interface SmazaniUzivatelskehoUctuContext {
	wizard: FormWizardHook<SmazaniUctuFormData>;
	step1: SmazaniUctuFormStep1;
	step2: SmazaniUctuFormStep2;
	step3: SmazaniUctuFormStep3;
	isSubmitted: boolean;
	isLoading: boolean;
	isConfirmModalOpen: IUseBoolean;
}

const ServiceContext =
	createContext<Nullable<SmazaniUzivatelskehoUctuContext>>(null);

interface SmazaniUzivatelskehoUctuProviderProps {
	children: ReactNode;
	onSubmitted: () => void;
}

export const SmazaniUzivatelskehoUctuContextProvider = ({
	children,
	onSubmitted,
}: SmazaniUzivatelskehoUctuProviderProps) => {
	const isConfirmModalOpen = useBoolean();
	const { axiosInstance } = useAxiosInstance();
	const submitMutation = usePoMutation({
		mutationFn: async (formData: SmazaniUctuFormData) =>
			removeAccountQuery(axiosInstance, { reason: formData.message }),
		onMutate: isConfirmModalOpen.setFalse,
		onSuccess: () => {
			wizard.resetForms();
			onSubmitted();
		},
	});

	const isDisabled = submitMutation.isLoading;

	const step1 = useFormStep1({ isDisabled });
	const step2 = useFormStep2({ isDisabled });
	const step3 = useFormStep3({ isDisabled });

	const wizard = useWizard<SmazaniUctuFormData>({
		steps: [step1, step2, step3],
		onSubmit: async (data) => submitMutation.mutate(data),
		persist: {
			key: 'smazani-uctu',
		},
	});

	const isSubmitted = submitMutation.isSuccess;
	const isLoading = submitMutation.isLoading;

	return (
		<ServiceContext.Provider
			value={{
				wizard,
				step1,
				step2,
				step3,
				isSubmitted,
				isLoading,
				isConfirmModalOpen,
			}}>
			{children}
		</ServiceContext.Provider>
	);
};

export const useSmazaniUzivatelskehoUctuContext =
	(): SmazaniUzivatelskehoUctuContext => {
		const context = useContext(ServiceContext);
		if (!context) {
			throw new Error('Context must be used within a ContextProvider');
		}
		return context;
	};
